import { CSSProperties } from 'react'
import {
  calculateRotationInDeg,
  getLength,
  getRotatedImageStyle,
  getZoomedStyle,
  ManualRotation,
} from '../../../../../features/prescription'
import { NaturalSize } from '../../../../../helpers/imageRotation'
import { ImageArea } from '../../../../../types/models/Presc'

// 自動傾き補正の核(getLength / calculateRotationInDeg / getZoomedStyle)は legacy の
// 共有 hook 側にあるものを import する。写すと補正が 2 実装になる。
// 共有 molecule を呼ばず器をここで組むのは、molecule が操作列にライトボックスを内包し、
// 器が aspect-ratio 固定 + overflow:hidden で拡大/パンを載せられないため。
// TODO: 核が helpers へ切り出されたら import 元を差し替える(組み立てはここに残す)

/** 画像枠に渡す寸法とスタイル。枠は overflow:hidden で、はみ出した紙面を切る */
export type ImageLayout = {
  /** <img> にそのまま渡す style */
  imageStyle: CSSProperties
  /** 枠の縦横比。null なら比を指定せず画像の高さに従う(読み込み前) */
  containerAspectRatio: string | null
  /** 表示中の角度(自動傾き補正 + 手動回転、deg) */
  rotation: number
}

type LayoutInput = {
  /** 表示中のページの処方箋存在領域。無ければ補正は掛からない */
  area: ImageArea | undefined
  naturalSize: NaturalSize | null
  manualRotation: ManualRotation
  /** 薬局設定(localStorage の isImageRotatable)。false なら原画像をそのまま出す */
  isImageRotatable: boolean
}

const RAW: CSSProperties = { width: '100%', display: 'block' }

/**
 * 表示中の 1 枚のレイアウトを決める。
 *
 * - 領域があり補正が有効なら、紙面領域に切り出して傾きを起こす(自動傾き補正)
 * - 手動回転中は領域への切り出しをやめ、回転後の外接矩形が枠に収まる形に切り替える
 *   (legacy と同じ規則。切り出したまま回すと紙面の外が入り込む)
 * - QR 直読のレコードは basic_result が無く領域も無いので、補正は掛からない
 */
export const getImageLayout = ({
  area,
  naturalSize,
  manualRotation,
  isImageRotatable,
}: LayoutInput): ImageLayout => {
  if (!naturalSize) {
    return { imageStyle: RAW, containerAspectRatio: null, rotation: 0 }
  }
  const { w, h } = naturalSize

  const canDeskew = isImageRotatable && area != null && area.length >= 3
  const autoRotation = canDeskew ? calculateRotationInDeg(area) : 0

  if (manualRotation !== 0) {
    const rotation = autoRotation + manualRotation
    const { imageStyle, containerAspectRatio } = getRotatedImageStyle(
      rotation,
      naturalSize
    )
    return { imageStyle, containerAspectRatio, rotation }
  }

  if (!canDeskew) {
    return {
      imageStyle: RAW,
      containerAspectRatio: `${w} / ${h}`,
      rotation: 0,
    }
  }

  // 領域の 1 辺(area[0]→area[1])を横、次の辺(area[1]→area[2])を縦とみなした比
  const areaAspectRatio =
    getLength(area[1], area[2], w, h) / getLength(area[0], area[1], w, h)

  return {
    imageStyle: getZoomedStyle(area, h / w),
    containerAspectRatio: `1 / ${areaAspectRatio}`,
    rotation: autoRotation,
  }
}

/** 倍率の段。紙面全体を収めるモードは作らない(余白が増えるだけで読みたい欄は大きくならない) */
export const ZOOM_STEPS = [1, 1.5, 2, 3, 4] as const

export const zoomIn = (scale: number) =>
  ZOOM_STEPS.find((step) => step > scale) ?? ZOOM_STEPS[ZOOM_STEPS.length - 1]

export const zoomOut = (scale: number) =>
  [...ZOOM_STEPS].reverse().find((step) => step < scale) ?? ZOOM_STEPS[0]

export type Offset = { x: number; y: number }

/**
 * パンの移動量をはみ出し量に収める。中央に置いた枠を倍率で拡げているので、
 * 片側のはみ出しは (枠 × 倍率 - 表示域) / 2。戻れなくなると「等倍に戻す」以外の退路が消える。
 */
export const clampOffset = (
  offset: Offset,
  { frame, box, scale }: { frame: Offset; box: Offset; scale: number }
): Offset => {
  const limit = (boxSide: number, frameSide: number) =>
    Math.max(0, (boxSide * scale - frameSide) / 2)
  // +0 を足して -0 を 0 に揃える(はみ出しが 0 のとき Math.max が -0 を返す)
  const clamp = (value: number, max: number) =>
    Math.min(max, Math.max(-max, value)) + 0
  return {
    x: clamp(offset.x, limit(box.x, frame.x)),
    y: clamp(offset.y, limit(box.y, frame.y)),
  }
}
